// BHP-1116: destroy a VPS — the confirmation for an action that cannot be undone.
//
// This is not restart. Restart interrupts a service that comes back; delete ends
// the machine at Hetzner (BHP-1113) and there is no starting it again. So the
// amber "are you sure?" of the power actions is too light here: the customer has
// to type the hostname, and only an exact match arms the button. A dialog, like
// RegisterDomainModal, with the keyboard contract from useDialogA11y.
//
// On success (204) the server is gone and our row is retired; this page would
// 404, so we navigate back to the overview, where the invalidated list no longer
// holds it. A refusal (502 Hetzner declined, 422 no account) leaves the server
// running and is shown inline, so the user stays put and can read why.
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toUserMessage } from '../../../shared/http/errorMessage';
import { useDialogA11y } from '../../../shared/ui/useDialogA11y';
import type { VpsSummary } from '../domain/vpsSummary';
import { useVpsDelete } from '../application/vpsDeleteMutation';

export default function VpsDeleteModal({
  server,
  overviewPath,
  onClose
}: {
  server: VpsSummary;
  overviewPath: string;
  onClose: () => void;
}): JSX.Element {
  const navigate = useNavigate();
  const { remove, isPending } = useVpsDelete(server.uuid);
  const [confirmation, setConfirmation] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const dialogRef = useDialogA11y<HTMLDivElement>(onClose);

  // Trimmed: a trailing space from an autocomplete or a copy-paste is not the
  // customer meaning a different server.
  const confirmed = confirmation.trim() === server.hostname;

  const onDelete = async (): Promise<void> => {
    setErrorMessage(null);
    try {
      await remove();
      navigate(overviewPath);
    } catch (error) {
      setErrorMessage(
        toUserMessage(
          error,
          'Nothing was deleted; the server is still running.'
        )
      );
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="vps-delete-title"
        className="w-full max-w-md overflow-hidden rounded-xl border border-solid border-slate-200 bg-white shadow-xl"
      >
        <div className="border-0 bg-rose-50 px-5 py-3">
          <h3
            id="vps-delete-title"
            className="text-base font-semibold text-rose-900"
          >
            Delete server
          </h3>
        </div>

        <div className="px-5 py-4 text-sm text-slate-700">
          <p className="m-0">
            This destroys <strong>{server.hostname}</strong> at Hetzner. The
            machine and its data are gone for good — this cannot be undone.
          </p>

          <label className="mt-4 block" htmlFor="vps-delete-confirm">
            <span className="mb-1 block text-xs font-medium text-slate-600">
              Type the hostname{' '}
              <span className="font-semibold text-slate-800">
                {server.hostname}
              </span>{' '}
              to confirm.
            </span>
            <input
              id="vps-delete-confirm"
              value={confirmation}
              onChange={(event) => {
                setConfirmation(event.target.value);
              }}
              className="w-full rounded-md border border-solid border-slate-300 px-3 py-1.5 text-sm text-slate-800 focus:border-rose-500 focus:outline-none"
            />
          </label>

          {errorMessage !== null ? (
            <p
              role="alert"
              className="mt-3 rounded border border-solid border-rose-200 bg-rose-50 p-2 text-rose-700"
            >
              {errorMessage}
            </p>
          ) : null}
        </div>

        <div className="flex justify-end gap-2 bg-slate-50 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isPending}
            className="rounded-md border border-solid border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              void onDelete();
            }}
            // Armed only by an exact hostname match: the typing is the
            // confirmation, so a mismatched or empty field cannot delete.
            disabled={isPending || !confirmed}
            className="rounded-md border-0 bg-rose-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-50"
          >
            {isPending ? 'Deleting…' : 'Delete server'}
          </button>
        </div>
      </div>
    </div>
  );
}
